import { enqueueSnackbar } from 'notistack';

import Button from '@mui/material/Button';

import { useBoolean } from 'src/hooks/use-boolean';

import { localStorageGetItem } from 'src/utils/local-storage';

import { useTranslate } from 'src/locales';
import { CallCompany } from 'src/services/call-lists/call-lists.types';
import { DEAL_STORAGE_KEYS } from 'src/services/deal-flow/deal-flow.types';
import { useGetAllPipelinesQuery } from 'src/services/deal-flow/deal-flow.service';

import Iconify from 'src/components/iconify';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';
import DealEditorModal from 'src/sections/deal-flow/components/deal-editor-modal';

import { FeatureType } from 'src/types/subscription.types';

// ----------------------------------------------------------------------

type Props = {
  contactId: string;
  contactName: string;
  company: CallCompany | null;
};

/**
 * Create deal from a call (JT1-1827): opens the deal editor with the title prefilled, and links the
 * new deal to the contact and their company. Uses the pipeline last opened on the deals page, else
 * the first one.
 */
export default function CreateDealButton({ contactId, contactName, company }: Props) {
  const { t } = useTranslate();
  const { canAccess } = useFeatures();
  const editor = useBoolean();

  const dealsEnabled = canAccess(FeatureType.DEAL_FLOW_ENABLED);
  const { currentData: pipelines } = useGetAllPipelinesQuery({ page: 1, perPage: 100 }, { skip: !dealsEnabled });

  if (!dealsEnabled) return null;

  const available = pipelines?.results ?? [];
  const stored = localStorageGetItem(DEAL_STORAGE_KEYS.SELECTED_PIPELINE_ID, '');
  const pipelineId = available.find((pipeline) => pipeline.id === stored)?.id ?? available[0]?.id;

  const open = () => {
    if (!pipelineId) {
      enqueueSnackbar(t('call_lists.deal.no_pipeline'), { variant: 'warning' });
      return;
    }
    editor.onTrue();
  };

  return (
    <>
      <Button size="small" variant="outlined" startIcon={<Iconify icon="solar:hand-money-bold" />} onClick={open} data-testid="call-create-deal">
        {t('call_lists.deal.create')}
      </Button>

      {editor.value && pipelineId && (
        <DealEditorModal
          open
          onClose={editor.onFalse}
          pipelineId={pipelineId}
          prefill={{
            title: company?.name || contactName,
            contactId,
            companyId: company?.id,
            linkedTo: company?.name
              ? t('call_lists.deal.linked_to_both', { person: contactName, company: company.name })
              : contactName,
          }}
        />
      )}
    </>
  );
}
